import { type ChangeEvent, type ComponentProps } from 'react';

import { cx } from '@/lib/cx';
import { estiloCampo } from '@/ui/primitives/estilos';

import { formatarBlocosChave, limparChave, TAMANHO_CHAVE } from './chave-blocos';

type CampoChaveAcessoProps = Omit<ComponentProps<'input'>, 'value' | 'onChange' | 'type'> & {
  /** Chave limpa (só letras e números). */
  valor: string;
  aoMudar: (chave: string) => void;
};

/** Quantos caracteres "de verdade" (sem os espaços dos blocos) há antes de `posicao`. */
function semEspacosAte(texto: string, posicao: number): number {
  return limparChave(texto.slice(0, posicao)).length;
}

/** Posição no texto em blocos depois de `n` caracteres da chave (cada 4 ganha um espaço). */
function posicaoNosBlocos(n: number): number {
  return n + Math.max(0, Math.ceil(n / 4) - 1) + (n > 0 && n % 4 === 0 ? 1 : 0);
}

/**
 * Entrada da chave de acesso em blocos de 4 (o "modo de entrada" da `ChaveAcesso`): aceita colar
 * com espaços, pontos ou quebras, mantém o cursor no lugar e mostra quantas das 44 posições há.
 * Combine com `Campo` para rótulo e erro; a validação (DV) é do schema do contrato.
 */
export function CampoChaveAcesso({ valor, aoMudar, className, ...props }: CampoChaveAcessoProps) {
  const mudar = (e: ChangeEvent<HTMLInputElement>) => {
    const campo = e.target;
    const antes = semEspacosAte(campo.value, campo.selectionStart ?? campo.value.length);
    const limpa = limparChave(campo.value);
    aoMudar(limpa);
    // Depois do re-render o valor vira blocos: o cursor volta para o mesmo caractere.
    requestAnimationFrame(() => {
      if (document.activeElement !== campo) return;
      const pos = Math.min(posicaoNosBlocos(antes), campo.value.length);
      campo.setSelectionRange(pos, pos);
    });
  };
  return (
    <span className="relative block">
      <input
        type="text"
        inputMode="text"
        autoComplete="off"
        spellCheck={false}
        {...props}
        value={formatarBlocosChave(valor)}
        onChange={mudar}
        className={cx(estiloCampo, 'chave-blocos h-controle pr-16 text-sm', className)}
      />
      <span
        aria-hidden
        className={cx(
          'tabular pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs',
          valor.length === TAMANHO_CHAVE ? 'text-sucesso' : 'text-texto-fraco',
        )}
      >
        {`${String(valor.length)}/${String(TAMANHO_CHAVE)}`}
      </span>
    </span>
  );
}
